import { AssetFolder, AssetMetadata } from '../models/Asset';
import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';

import AuthService from '../services/AuthService';
import Config from '../config';
import { FileContentAttributes } from '../models/FileContent';
import HttpClient from '../services/HttpClient';
import { Observable } from 'rxjs';
import { ScreenCaptureItem } from '../../content/models/LessonComponentContent';
import { TransferProgressEvent } from '@azure/core-http';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { useLinkFile } from './useLinkFile';

/**
 * Name of Azure container where theme media is stored. We use the URL instead
 * of the Design DS redirect to provide live preview.
 */
export const DOMAIN_DESIGN_CONTAINER = 'domain-design';

export class FileUploadObservable extends Observable<FileUploadProgress> {}
export class VideoEncodeObservable extends Observable<number | AssetMetadata> {} //* to notify the timer ID to the subscriber

export interface AssetUploading {
  metadata: AssetMetadata;
  abortController: AbortController;
  uploadObservable: FileUploadObservable | null;
  encodeObservable: VideoEncodeObservable | null;
}

export interface FileUploadProgress {
  value: number; //* %
}

export function useAssetStorage(tenant: string) {
  const { linkFileContent } = useLinkFile();

  async function upload(
    files: File[],
    type: AssetFolder,
    assetkeys?: string[],
    hidden?: boolean
  ) {
    return Promise.all(
      files.map((file, index) =>
        uploadFileAsync(file, type, assetkeys?.[index], hidden)
      )
    );
  }

  async function getContainerClient(containerName: string) {
    const token = AuthService.getAccessToken();
    const { data } = await HttpClient.get<{ sas: string }>(
      `${Config.ASSET_DS}/api/sas?container=${containerName}`,
      {
        authorization: `Bearer ${token}`
      }
    );
    const blobServiceClient = new BlobServiceClient(
      `https://${Config.STORAGE_ACCOUNT}.blob.core.windows.net/?${data.sas}`
    );
    return blobServiceClient.getContainerClient(containerName);
  }

  function getAssetkey() {
    return nanoid();
  }

  function getAssetBlobPath(
    assetFolder: AssetFolder,
    file: File,
    blobName: string
  ) {
    if (file.type.startsWith('video/')) {
      return blobName;
    } else if (assetFolder === 'learner_assets') {
      return `${assetFolder}/${AuthService.Userkey}/${blobName}`;
    } else {
      return `${assetFolder}/${blobName}`;
    }
  }

  async function uploadFileAsync(
    file: File,
    assetFolder: AssetFolder,
    assetkey?: string,
    hidden?: boolean
  ) {
    const containerClient = await getContainerClient(
      file.type.startsWith('video/')
        ? 'upload' //* for the encoder to pick up the blob from the 'upload' folder.
        : tenant
    );

    const blobName = assetkey || getAssetkey();
    const blobPath = getAssetBlobPath(assetFolder, file, blobName);

    //Notify API when uploading media file to azure
    const input: FileContentAttributes = {
      label: blobName, //label should be nanoId
      type: file.type,
      userkey: AuthService.Userkey,
      container: containerClient.containerName,
      location: blobPath,
      bytes: file.size
    };
    await linkFileContent(input);

    //Upload blob into azure
    return uploadBlob(file, containerClient, blobName, blobPath, hidden);
  }

  /** Upload a blob to the skin directory. */
  async function uploadSkinFile(file: File) {
    if (!file) {
      return;
    }
    const containerClient = await getContainerClient(DOMAIN_DESIGN_CONTAINER);
    const blobName = file.name;
    const blobPath = `${AuthService.Tenant}/${blobName}`;
    return uploadBlob(file, containerClient, blobName, blobPath);
  }

  async function uploadBlob(
    file: File,
    containerClient: ContainerClient,
    blobName: string,
    blobPath: string,
    hidden?: boolean
  ): Promise<AssetUploading> {
    const blockBlobClient = containerClient.getBlockBlobClient(blobPath);
    const fileSize = file.size;
    const now = dayjs();
    const abortController = new AbortController();
    const assetUploading: AssetUploading = {
      metadata: {
        assetkey: blobName,
        assetpath: blobPath,
        contentType: file.type,
        filename: file.name,
        size: fileSize,
        owner: AuthService.Userkey,
        hidden: hidden || false,
        dateCreated: now.toISOString()
      },
      abortController,
      uploadObservable: new FileUploadObservable((subscriber) => {
        blockBlobClient
          .uploadData(file, {
            abortSignal: abortController.signal,
            blobHTTPHeaders: { blobContentType: file.type },
            metadata: {
              filename: encodeURIComponent(file.name),
              owner: `${AuthService.Userkey}`,
              hidden: hidden ? 'true' : 'false',
              size: file.size.toString()
            },
            onProgress: (progress: TransferProgressEvent) => {
              subscriber.next({
                value: (progress.loadedBytes / fileSize) * 100
              });
            }
          })
          .then((value) => {
            subscriber.complete();
          })
          .catch((reason) => {
            if (reason.name === 'AbortError') {
              //* https://devblogs.microsoft.com/azure-sdk/how-to-use-abort-signals-to-cancel-operations-in-the-azure-sdk-for-javascript-typescript/
              //* Upload cancelled by the user, so no need to show an error message.
              return;
            }

            subscriber.error(reason);
          });
      }),
      encodeObservable: null
    };
    return assetUploading;
  }

  /**
   * Upload a base64 string as a file, returning a promise that is resolved when
   * the file has finished uploading.
   * @param items Screen capture items to upload.
   * @param type Folder to upload to (author, learner).
   */
  async function uploadBase64(
    items: ScreenCaptureItem[],
    type: AssetFolder
  ): Promise<AssetUploading[]> {
    const files = () =>
      items.map((item, index) => {
        const fileName = `${dayjs(new Date()).format('DD/MM/YYYY')}-${
          index + 1
        }`;
        return base64ToFile(item.image, fileName);
      });
    const assetkeys = items.map((item) => item.id);
    return upload(files(), type, assetkeys);
  }

  function base64ToFile(base64: string, fileName: string): File {
    const mime = /^data:(.*?);base64,/.exec(base64)[1];
    const bstr = atob(base64.split(',')[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], fileName, { type: mime });
  }

  async function uploadCaptures(
    items: ScreenCaptureItem[],
    folderType: AssetFolder
  ) {
    const uploadItems = items?.filter((item) => !item.image.includes('https'));
    await uploadBase64(uploadItems, folderType)
      .then((assets) =>
        assets.map((asset) => {
          const subscription = asset.uploadObservable.subscribe({
            error: (error) => {
              subscription.unsubscribe();
              console.error(error);
            },
            complete: () => {
              subscription.unsubscribe();
            }
          });
          return asset.metadata;
        })
      )
      .catch((err) => console.error(err));
  }

  return {
    upload,
    uploadSkinFile,
    uploadCaptures
  };
}
